import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  LoginId,
  LoginV2ApplicationErrors,
  LoginV2Options,
  LoginV2ValidationErrors,
  LogoutOptions,
  RawHttpResponse,
  RegisterV2ApplicationErrors,
  RegisterV2Options,
  RegisterV2ValidationErrors,
  SignOnOptions,
  SignOnResponse,
  StateMachineResponse,
  ValidateEmailResponse,
  changePassword as universalChangePassword,
  loginV2 as universalLoginV2,
  logout as universalLogout,
  registerV2 as universalRegisterV2,
  signOn as universalSignOn,
  validateEmail as universalValidateEmail,
} from './iam-authentication-v1-authentication-authentication.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/identity' };

export function registerV2(httpClient: HttpClient): RegisterV2Signature {
  return (loginId: LoginId, options?: RegisterV2Options) =>
    universalRegisterV2(
      loginId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RegisterV2Signature {
  /**
   * Registers a new member.
   *
   * Typically, after a successful registration, you generate and use member tokens for the
   * registered member so that subsequent API calls are called as part of a member session.
   * Use the `sessionToken` in the response to [get the site member's access and refresh tokens](https://dev.wix.com/docs/go-headless/coding/rest-api/visitors-and-members/handle-members-with-custom-login#get-the-site-members-access-and-refresh-tokens).
   *
   * If the email used to register the member already exists as a contact email, the registering
   * member need to verify the email address using a code that is sent to the address.
   * @param - Identifier of the registering member.
   */
  (loginId: LoginId, options?: RegisterV2Options): Promise<
    NonNullablePaths<
      StateMachineResponse,
      | `state`
      | `identity.connections`
      | `identity.connections.${number}.idpConnection.idpConnectionId`
      | `identity.connections.${number}.idpConnection.idpUserId`
      | `identity.connections.${number}.authenticatorConnection.authenticatorConnectionId`
      | `identity.connections.${number}.authenticatorConnection.reEnrollmentRequired`
      | `identity.identityProfile.emails`
      | `identity.identityProfile.phones`
      | `identity.identityProfile.labels`
      | `identity.identityProfile.privacyStatus`
      | `identity.identityProfile.customFields`
      | `identity.identityProfile.customFields.${number}.name`
      | `identity.identityProfile.customFields.${number}.value.strValue`
      | `identity.identityProfile.customFields.${number}.value.numValue`
      | `identity.identityProfile.customFields.${number}.value.boolValue`
      | `identity.identityProfile.secondaryEmails`
      | `identity.identityProfile.secondaryEmails.${number}.email`
      | `identity.identityProfile.secondaryEmails.${number}.tag`
      | `identity.identityProfile.phonesV2`
      | `identity.identityProfile.phonesV2.${number}.phone`
      | `identity.identityProfile.phonesV2.${number}.tag`
      | `identity.identityProfile.addresses`
      | `identity.identityProfile.addresses.${number}.tag`
      | `identity.email.address`
      | `identity.email.isVerified`
      | `identity.status.name`
      | `identity.status.reasons`
      | `identity.factors`
      | `identity.factors.${number}.factorId`
      | `identity.factors.${number}.type`
      | `identity.factors.${number}.status`,
      5
    > & {
      __applicationErrorsType?: RegisterV2ApplicationErrors;
      __validationErrorsType?: RegisterV2ValidationErrors;
    }
  >;
}

export function validateEmail(httpClient: HttpClient): ValidateEmailSignature {
  return (email: string) =>
    universalValidateEmail(
      email,
      // @ts-ignore
      { httpClient }
    );
}

interface ValidateEmailSignature {
  /** */
  (email: string): Promise<
    NonNullablePaths<
      ValidateEmailResponse,
      `valid` | `failure.reason` | `formatInvalid`,
      1
    >
  >;
}

export function loginV2(httpClient: HttpClient): LoginV2Signature {
  return (loginId: LoginId, options?: LoginV2Options) =>
    universalLoginV2(
      loginId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface LoginV2Signature {
  /**
   * Logs in an existing member.
   *
   * Typically, after a successful login, you generate and use member tokens for the
   * logged-in member so that subsequent API calls are called as part of a member session.
   * Use the `sessionToken` in the response to [get the site member's access and refresh tokens](https://dev.wix.com/docs/go-headless/coding/rest-api/visitors-and-members/handle-members-with-custom-login#get-the-site-members-access-and-refresh-tokens).
   * @param - Identifier of identity logging in.
   */
  (loginId: LoginId, options?: LoginV2Options): Promise<
    NonNullablePaths<
      StateMachineResponse,
      | `state`
      | `identity.connections`
      | `identity.connections.${number}.idpConnection.idpConnectionId`
      | `identity.connections.${number}.idpConnection.idpUserId`
      | `identity.connections.${number}.authenticatorConnection.authenticatorConnectionId`
      | `identity.connections.${number}.authenticatorConnection.reEnrollmentRequired`
      | `identity.identityProfile.emails`
      | `identity.identityProfile.phones`
      | `identity.identityProfile.labels`
      | `identity.identityProfile.privacyStatus`
      | `identity.identityProfile.customFields`
      | `identity.identityProfile.customFields.${number}.name`
      | `identity.identityProfile.customFields.${number}.value.strValue`
      | `identity.identityProfile.customFields.${number}.value.numValue`
      | `identity.identityProfile.customFields.${number}.value.boolValue`
      | `identity.identityProfile.secondaryEmails`
      | `identity.identityProfile.secondaryEmails.${number}.email`
      | `identity.identityProfile.secondaryEmails.${number}.tag`
      | `identity.identityProfile.phonesV2`
      | `identity.identityProfile.phonesV2.${number}.phone`
      | `identity.identityProfile.phonesV2.${number}.tag`
      | `identity.identityProfile.addresses`
      | `identity.identityProfile.addresses.${number}.tag`
      | `identity.email.address`
      | `identity.email.isVerified`
      | `identity.status.name`
      | `identity.status.reasons`
      | `identity.factors`
      | `identity.factors.${number}.factorId`
      | `identity.factors.${number}.type`
      | `identity.factors.${number}.status`,
      5
    > & {
      __applicationErrorsType?: LoginV2ApplicationErrors;
      __validationErrorsType?: LoginV2ValidationErrors;
    }
  >;
}

export function changePassword(
  httpClient: HttpClient
): ChangePasswordSignature {
  return (newPassword: string) =>
    universalChangePassword(
      newPassword,
      // @ts-ignore
      { httpClient }
    );
}

interface ChangePasswordSignature {
  /**
   * Changes the password of a logged-in member. The member must have a valid session to use this method.
   *
   * The member's session remains valid after password change.
   *
   * The new password must meet the site's configured password requirements for length
   * and complexity. These requirements are enforced automatically when processing the request.
   * @param - New password to set for the logged-in member.
   *
   * The password must meet the site's configured password requirements for length and complexity.
   */
  (newPassword: string): Promise<void>;
}

export function signOn(httpClient: HttpClient): SignOnSignature {
  return (loginId: LoginId, options?: SignOnOptions) =>
    universalSignOn(
      loginId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SignOnSignature {
  /**
   * Authenticates a member without credentials and returns a session token.
   *
   * This method provides authentication that automatically creates or updates
   * member accounts as needed during the authentication process.
   * Use this only for trusted integrations and scenarios where you already have verified user information.
   * @param - Member identifier for authentication.
   *
   * Currently only email addresses are supported.
   */
  (loginId: LoginId, options?: SignOnOptions): Promise<
    NonNullablePaths<
      SignOnResponse,
      | `sessionToken`
      | `identity.connections`
      | `identity.connections.${number}.idpConnection.idpConnectionId`
      | `identity.connections.${number}.idpConnection.idpUserId`
      | `identity.connections.${number}.authenticatorConnection.authenticatorConnectionId`
      | `identity.connections.${number}.authenticatorConnection.reEnrollmentRequired`
      | `identity.identityProfile.emails`
      | `identity.identityProfile.phones`
      | `identity.identityProfile.labels`
      | `identity.identityProfile.privacyStatus`
      | `identity.identityProfile.customFields`
      | `identity.identityProfile.customFields.${number}.name`
      | `identity.identityProfile.customFields.${number}.value.strValue`
      | `identity.identityProfile.customFields.${number}.value.numValue`
      | `identity.identityProfile.customFields.${number}.value.boolValue`
      | `identity.identityProfile.secondaryEmails`
      | `identity.identityProfile.secondaryEmails.${number}.email`
      | `identity.identityProfile.secondaryEmails.${number}.tag`
      | `identity.identityProfile.phonesV2`
      | `identity.identityProfile.phonesV2.${number}.phone`
      | `identity.identityProfile.phonesV2.${number}.tag`
      | `identity.identityProfile.addresses`
      | `identity.identityProfile.addresses.${number}.tag`
      | `identity.email.address`
      | `identity.email.isVerified`
      | `identity.status.name`
      | `identity.status.reasons`
      | `identity.factors`
      | `identity.factors.${number}.factorId`
      | `identity.factors.${number}.type`
      | `identity.factors.${number}.status`,
      5
    >
  >;
}

export function logout(httpClient: HttpClient): LogoutSignature {
  return (options?: LogoutOptions) =>
    universalLogout(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface LogoutSignature {
  /**
   * Terminates the member's session and clears authentication cookies.
   *
   * Returns the following:
   * - `body`: HTML content for the logout response
   * - `statusCode`: HTTP status code (200 for success, 302 for redirect)
   * - `headers`: Array of key-value pairs including:
   * - `Set-Cookie` headers to remove all session cookies
   * - `Location` header (when `postLogoutRedirectUri` is provided, with 302 status)
   */
  (options?: LogoutOptions): Promise<
    NonNullablePaths<
      RawHttpResponse,
      `body` | `headers` | `headers.${number}.key` | `headers.${number}.value`,
      2
    >
  >;
}

export {
  AccountInfo,
  Address,
  AddressTag,
  AddressWrapper,
  Authentication,
  AuthenticatorConnection,
  CallVerifyData,
  CaptchaDecisionReason,
  CaptchaFlow,
  CaptchaIdentityType,
  CaptchaKind,
  CaptchaOutcome,
  CaptchaResult,
  CaptchaToken,
  CaptchaTokenTokenOneOf,
  CaptchaVerification,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ConnectIdpRequest,
  Connection,
  ConnectionTypeOneOf,
  CustomField,
  CustomValue,
  CustomValueValueOneOf,
  DeliveryMethod,
  Email,
  EmailTag,
  EmailValidationFailure,
  ExternalEmailVerifyData,
  Factor,
  FactorStatus,
  FactorType,
  GetWebauthnChallengeRequest,
  GetWebauthnChallengeResponse,
  HeadersEntry,
  IdentificationData,
  IdentificationDataIdOneOf,
  Identity,
  IdentityProfile,
  IdpConnection,
  ListValue,
  LoginCallbackRequest,
  LoginId,
  LoginIdTypeOneOf,
  LoginV2Options,
  LoginV2Request,
  LoginWithIdpConnectionRedirectRequest,
  LoginWithIdpConnectionRequest,
  LoginWithIdpConnectionTokenParamsRequest,
  LogoutOptions,
  LogoutRequest,
  MapValue,
  MessageEnvelope,
  Metadata,
  MfaChallengeData,
  MfaReason,
  Organization,
  PathParametersEntry,
  Phone,
  PhoneTag,
  PrivacyStatus,
  PushChallengeData,
  PushVerifyData,
  QueryParametersEntry,
  RawHttpRequest,
  RawHttpResponse,
  Reason,
  RecaptchaForClientSiteKey,
  RecoveryCodeVerifyData,
  RegisterV2Options,
  RegisterV2Request,
  RequireFirstFactorData,
  RequireMfaData,
  SecondaryEmail,
  SelectOrganizationRequest,
  SelectOrganizationResponse,
  SignOnOptions,
  SignOnRequest,
  SignOnResponse,
  SmsVerifyData,
  StateMachineResponse,
  StateMachineResponseStateDataOneOf,
  StateType,
  Status,
  StatusName,
  StatusV2,
  TenantType,
  TotpVerifyData,
  V1CustomValue,
  V1CustomValueValueOneOf,
  V1Factor,
  V1ListValue,
  V1MapValue,
  V1Reason,
  ValidateEmailRequest,
  ValidateEmailResponse,
  VerificationChallenge,
  VerificationChallengeFactorChallengeDataOneOf,
  VerifyRequest,
  VerifyRequestFactorDataOneOf,
  WebAuthnChallengeData,
  WebAuthnVerifyData,
  WebhookIdentityType,
} from './iam-authentication-v1-authentication-authentication.universal.js';
